import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useWebRTCMesh — peer-mesh audio + data-channel hook layered on top of the
 * existing /api/collab WebSocket (which we re-use as a signaling bus).
 *
 *   const mesh = useWebRTCMesh({
 *     me,           // {id, name} from useCollab (signaling identity)
 *     peers,        // [{id, name}] from useCollab (current room roster)
 *     send,         // collab.send(type, payload, { to })
 *     enabled,      // boolean — only run when in a connected room
 *     maxPeers,     // hard cap on mesh size (default 4)
 *     onPeerData,   // (peerId, message) — fires for any incoming data-channel msg
 *   });
 *
 *   mesh.peerStreams      // { [peerId]: { audioState, connectionState } }
 *   mesh.localMicEnabled  // boolean — true when getUserMedia is live
 *   mesh.toggleMic()      // start/stop mic capture + add/remove tracks
 *   mesh.setPeerMuted(peerId, muted)  // mute incoming audio from a peer
 *   mesh.sendData(type, payload)      // broadcast on every open data channel
 *   mesh.handleSignal(msg)            // call this from useCollab's onMessage
 *
 * Architecture notes:
 *  - **Mesh topology** (every peer ↔ every peer). Fine up to ~6 peers; beyond
 *    that an SFU is the right choice. We clamp at `maxPeers` so the UI never
 *    builds connections it can't sustain.
 *  - **Polite-peer pattern** (MDN "Perfect Negotiation") avoids offer/answer
 *    glare. The peer whose `me.id` sorts lower wins ties (impolite).
 *  - **Signaling envelope**:  { type: "rtc:offer" | "rtc:answer" | "rtc:ice",
 *                               payload: { sdp | candidate }, to: peerId }
 *    The backend forwards directed (to:) messages 1:1 instead of broadcasting.
 *  - **Audio sink**: incoming MediaStream tracks attach to an in-DOM <audio>
 *    element appended to `document.body` (display:none) so the browser actually
 *    plays remote audio. Most browsers REQUIRE an HTMLAudioElement reference
 *    even when you only want the Web Audio output — otherwise the stream is
 *    decoded but silent. We keep the element hidden so the UI is unaffected.
 *  - **Data channel** ("state-sync") opens by the impolite peer; carries
 *    transport/mixer mutations with <30 ms latency vs the ~100 ms WS path.
 */

const PUBLIC_STUN = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

/** Build the iceServers config — public STUN, plus optional TURN if the
 *  user has set `REACT_APP_TURN_URL/_USERNAME/_PASSWORD` in /app/frontend/.env.
 *
 *  TURN matters because ~20% of consumer NATs (symmetric NAT, double-NAT,
 *  carrier-grade NAT) can't be punched by STUN alone — the mesh will silently
 *  fail to connect those peers. With TURN as a relay fallback the mesh works
 *  everywhere at the cost of higher latency + bandwidth on the relay server.
 *
 *  We read env vars at module load (CRA inlines them at build time so they're
 *  static). Missing → STUN-only (current behaviour from iter 37). */
const STUN_SERVERS = (() => {
  const url = process.env.REACT_APP_TURN_URL;
  const username = process.env.REACT_APP_TURN_USERNAME;
  const credential = process.env.REACT_APP_TURN_PASSWORD;
  if (url && username && credential) {
    // TURN URLs may be comma-separated to give multiple regions; let RTCPeer
    // Connection round-robin through them.
    const urls = url.split(",").map((s) => s.trim()).filter(Boolean);
    return [...PUBLIC_STUN, { urls, username, credential }];
  }
  return PUBLIC_STUN;
})();

const RTC_OFFER = "rtc:offer";
const RTC_ANSWER = "rtc:answer";
const RTC_ICE = "rtc:ice";

/** Polite peer wins ties. Compare ids lexicographically. Lower id = impolite. */
function isImpolite(myId, peerId) {
  return String(myId) < String(peerId);
}

export default function useWebRTCMesh({
  me,
  peers,
  send,
  enabled,
  maxPeers = 4,
  onPeerData,
  onPeerBinary,
}) {
  // peerStates is the *react-rendered* mirror of the underlying peer Map so
  // the UI can react to connection-state changes. The Map is the source of
  // truth; setPeerStates is called whenever a peer's slot mutates.
  const [peerStates, setPeerStates] = useState({});
  const [localMicEnabled, setLocalMicEnabled] = useState(false);
  const [localStreamErr, setLocalStreamErr] = useState(null);

  // Map<peerId, { pc, audioEl, remoteStream, dataChannel, makingOffer, ignoreOffer, muted }>
  const peersRef = useRef(new Map());
  // The local mic MediaStream — null until the user clicks "Talk".
  const localStreamRef = useRef(null);
  // Stable refs so callbacks don't need to be re-created on every render.
  const sendRef = useRef(send);
  const meRef = useRef(me);
  const onDataRef = useRef(onPeerData);
  const onBinaryRef = useRef(onPeerBinary);
  useEffect(() => { sendRef.current = send; }, [send]);
  useEffect(() => { meRef.current = me; }, [me]);
  useEffect(() => { onDataRef.current = onPeerData; }, [onPeerData]);
  useEffect(() => { onBinaryRef.current = onPeerBinary; }, [onPeerBinary]);

  // Re-render trigger — flatten peersRef into a plain object for setPeerStates.
  const syncReactState = useCallback(() => {
    const out = {};
    peersRef.current.forEach((slot, id) => {
      out[id] = {
        connectionState: slot.pc?.connectionState || "new",
        iceConnectionState: slot.pc?.iceConnectionState || "new",
        hasRemoteAudio: !!slot.remoteStream && slot.remoteStream.getAudioTracks().length > 0,
        muted: !!slot.muted,
        dataChannelOpen: slot.dataChannel?.readyState === "open",
        rttMs: typeof slot.lastRttMs === "number" ? slot.lastRttMs : null,
        rttAt: slot.lastRttAt || null,
      };
    });
    setPeerStates(out);
  }, []);

  /** Create a fresh slot + RTCPeerConnection for a peer. Returns the slot. */
  const ensurePeer = useCallback((peerId) => {
    let slot = peersRef.current.get(peerId);
    if (slot) return slot;
    const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS });

    // Hidden <audio> sink so the browser actually plays remote tracks.
    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    audioEl.style.display = "none";
    audioEl.setAttribute("data-peer-audio", peerId);
    document.body.appendChild(audioEl);

    slot = {
      pc,
      audioEl,
      remoteStream: null,
      dataChannel: null,
      makingOffer: false,
      ignoreOffer: false,
      muted: false,
    };
    peersRef.current.set(peerId, slot);

    // ── Outbound: when we add a track / renegotiate, fire an offer.
    pc.onnegotiationneeded = async () => {
      const myId = meRef.current?.id;
      const impolite = isImpolite(myId, peerId);
      // Only the impolite peer initiates offers; the polite peer waits.
      if (!impolite) return;
      try {
        slot.makingOffer = true;
        await pc.setLocalDescription();
        sendRef.current?.(RTC_OFFER, { sdp: pc.localDescription }, { to: peerId });
      } catch (e) {
        console.warn("[rtc] negotiate failed", peerId, e);
      } finally {
        slot.makingOffer = false;
      }
    };

    pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        sendRef.current?.(RTC_ICE, { candidate: ev.candidate.toJSON() }, { to: peerId });
      }
    };

    pc.ontrack = (ev) => {
      // First track sets remoteStream; subsequent tracks join the same stream.
      const stream = ev.streams[0] || new MediaStream([ev.track]);
      slot.remoteStream = stream;
      slot.audioEl.srcObject = stream;
      slot.audioEl.muted = slot.muted;
      // Some browsers require an explicit play() after srcObject set.
      slot.audioEl.play?.().catch(() => { /* autoplay restriction — user has already gestured */ });
      syncReactState();
    };

    pc.onconnectionstatechange = () => {
      syncReactState();
      // GC on permanent failure.
      if (pc.connectionState === "failed" || pc.connectionState === "closed") {
        // Don't actively destroy here — the cleanup loop tears down on leave.
      }
    };

    pc.oniceconnectionstatechange = syncReactState;

    pc.ondatachannel = (ev) => {
      attachDataChannel(slot, ev.channel);
      syncReactState();
    };

    // Impolite peer also opens the data channel proactively. Polite peer
    // receives it via `pc.ondatachannel`.
    const myId = meRef.current?.id;
    if (isImpolite(myId, peerId)) {
      const dc = pc.createDataChannel("state-sync", { ordered: true });
      attachDataChannel(slot, dc);
    }

    // Add local mic tracks if we already had them captured before this peer joined.
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    }

    syncReactState();
    return slot;
    // attachDataChannel is a hoisted fn that only touches refs — adding it
    // would churn this callback identity every render for no benefit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncReactState]);

  /** Wire data-channel events. Mutates `slot.dataChannel`. */
  function attachDataChannel(slot, dc) {
    slot.dataChannel = dc;
    // CRDT updates ride as binary ArrayBuffers — force the right delivery type
    // so `ev.data` arrives as ArrayBuffer instead of Blob (Chromium default).
    try { dc.binaryType = "arraybuffer"; } catch (e) { void e; }
    dc.onopen = () => {
      // Re-sync UI when channel opens — the indicator dot can flip green.
      const peerId = [...peersRef.current.entries()].find(([, s]) => s === slot)?.[0];
      if (peerId) {
        peersRef.current.set(peerId, slot);
      }
      // Sync state pushed up via the outer setPeerStates loop.
    };
    dc.onmessage = (ev) => {
      // Binary path: Yjs CRDT update bytes (or any other binary payload).
      // Fire the binary callback and skip the JSON parse.
      if (ev.data instanceof ArrayBuffer) {
        try {
          const peerId = [...peersRef.current.entries()].find(([, s]) => s === slot)?.[0];
          onBinaryRef.current?.(peerId, new Uint8Array(ev.data));
        } catch (e) { console.warn("[rtc] onPeerBinary threw", e); }
        return;
      }
      // String path: JSON messages. We intercept latency-probe envelopes
      // before delegating to the public onPeerData callback so apps don't
      // see our private "__ping"/"__pong" tags.
      let data;
      try { data = JSON.parse(ev.data); } catch { return; }
      if (!data || typeof data !== "object") return;
      // Latency probe: reply to a ping with a pong carrying the original ts.
      // The sender computes RTT = Date.now() - ts on receipt.
      if (data.__ping && typeof data.__ping === "number") {
        try { dc.send(JSON.stringify({ __pong: data.__ping })); } catch (e) { void e; }
        return;
      }
      if (data.__pong && typeof data.__pong === "number") {
        const rtt = Math.max(0, Date.now() - data.__pong);
        slot.lastRttMs = rtt;
        slot.lastRttAt = Date.now();
        syncReactState();
        return;
      }
      try {
        const peerId = [...peersRef.current.entries()].find(([, s]) => s === slot)?.[0];
        onDataRef.current?.(peerId, data);
      } catch (e) { console.warn("[rtc] onPeerData threw", e); }
    };
    dc.onclose = () => { /* state diff captured on next syncReactState tick */ };
  }

  /** Tear down a single peer slot — pc.close, remove audio el, drop from map. */
  const dropPeer = useCallback((peerId) => {
    const slot = peersRef.current.get(peerId);
    if (!slot) return;
    try { slot.dataChannel?.close(); } catch (e) { void e; }
    try { slot.pc?.close(); } catch (e) { void e; }
    if (slot.audioEl) {
      try { slot.audioEl.srcObject = null; } catch (e) { void e; }
      slot.audioEl.remove();
    }
    peersRef.current.delete(peerId);
    syncReactState();
  }, [syncReactState]);

  /** Handle an inbound signaling message. Called by parent from collab.onMessage.
   *  Recognises `rtc:offer | rtc:answer | rtc:ice`. */
  const handleSignal = useCallback(async (msg) => {
    if (!msg || typeof msg !== "object") return;
    const t = msg.type;
    if (t !== RTC_OFFER && t !== RTC_ANSWER && t !== RTC_ICE) return;
    const peerId = msg.from_id;
    if (!peerId) return;
    const slot = ensurePeer(peerId);
    const pc = slot.pc;
    const myId = meRef.current?.id;
    const polite = !isImpolite(myId, peerId);

    try {
      if (t === RTC_OFFER) {
        // Perfect-negotiation pattern: if we're already making an offer or
        // have a local description that conflicts, decide who yields by
        // politeness.
        const offerCollision = slot.makingOffer || pc.signalingState !== "stable";
        slot.ignoreOffer = !polite && offerCollision;
        if (slot.ignoreOffer) return;
        await pc.setRemoteDescription(msg.payload?.sdp);
        await pc.setLocalDescription();
        sendRef.current?.(RTC_ANSWER, { sdp: pc.localDescription }, { to: peerId });
      } else if (t === RTC_ANSWER) {
        if (pc.signalingState === "have-local-offer") {
          await pc.setRemoteDescription(msg.payload?.sdp);
        }
      } else if (t === RTC_ICE) {
        const cand = msg.payload?.candidate;
        if (!cand) return;
        try { await pc.addIceCandidate(cand); }
        catch (err) {
          if (!slot.ignoreOffer) console.warn("[rtc] addIceCandidate failed", err);
        }
      }
    } catch (e) {
      console.warn("[rtc] handleSignal error", t, peerId, e);
    }
  }, [ensurePeer]);

  /** Toggle local mic capture. When enabling, opens getUserMedia + adds
   *  tracks to every existing peer connection. When disabling, stops tracks
   *  and removes senders. */
  const toggleMic = useCallback(async () => {
    if (localStreamRef.current) {
      // Stop & remove from all peers.
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      peersRef.current.forEach((slot) => {
        slot.pc.getSenders().forEach((s) => {
          if (s.track && s.track.kind === "audio") {
            try { slot.pc.removeTrack(s); } catch (e) { void e; }
          }
        });
      });
      localStreamRef.current = null;
      setLocalMicEnabled(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      localStreamRef.current = stream;
      setLocalStreamErr(null);
      // Push tracks into every peer; onnegotiationneeded fires automatically.
      peersRef.current.forEach((slot) => {
        stream.getTracks().forEach((t) => slot.pc.addTrack(t, stream));
      });
      setLocalMicEnabled(true);
    } catch (e) {
      console.warn("[rtc] getUserMedia failed", e);
      setLocalStreamErr(e?.message || "Microphone permission denied");
    }
  }, []);

  /** Mute / unmute incoming audio from a specific peer. */
  const setPeerMuted = useCallback((peerId, muted) => {
    const slot = peersRef.current.get(peerId);
    if (!slot) return;
    slot.muted = !!muted;
    if (slot.audioEl) slot.audioEl.muted = !!muted;
    syncReactState();
  }, [syncReactState]);

  /** Broadcast a JSON message on every open data channel. */
  const sendData = useCallback((type, payload) => {
    const msg = JSON.stringify({ type, payload });
    peersRef.current.forEach((slot) => {
      if (slot.dataChannel?.readyState === "open") {
        try { slot.dataChannel.send(msg); } catch (e) { void e; }
      }
    });
  }, []);

  /** Broadcast raw binary bytes (e.g. a Yjs CRDT update) on every open
   *  data channel. Returns the number of peers we successfully shipped to —
   *  useful for callers that want to decide whether to fall back to WS. */
  const sendBinary = useCallback((bytes) => {
    if (!(bytes instanceof Uint8Array) && !(bytes instanceof ArrayBuffer)) return 0;
    // RTCDataChannel.send accepts ArrayBuffer — normalize Uint8Array.
    const buf = bytes instanceof Uint8Array ? bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) : bytes;
    let sent = 0;
    peersRef.current.forEach((slot) => {
      if (slot.dataChannel?.readyState === "open") {
        try {
          slot.dataChannel.send(buf);
          sent += 1;
        } catch (e) {
          console.warn("[rtc] sendBinary failed", e);
        }
      }
    });
    return sent;
  }, []);

  // Whenever the room roster changes, sync our mesh:
  //  - Open a connection to any new peer (impolite side initiates via offer).
  //  - Drop peers that have left the room.
  //  - Hard-cap at maxPeers so a flood doesn't tank the browser.
  useEffect(() => {
    if (!enabled || !me?.id) return undefined;

    const seen = new Set();
    const peerArr = Array.isArray(peers) ? peers.slice(0, Math.max(0, maxPeers)) : [];
    peerArr.forEach((p) => {
      if (!p?.id || p.id === me.id) return;
      seen.add(p.id);
      ensurePeer(p.id);
    });

    // Drop peers that left (or were truncated past maxPeers).
    [...peersRef.current.keys()].forEach((existingId) => {
      if (!seen.has(existingId)) dropPeer(existingId);
    });

    syncReactState();
    return undefined;
  }, [peers, me, enabled, maxPeers, ensurePeer, dropPeer, syncReactState]);

  // Periodically refresh react state so connection/ice state transitions
  // surface to the UI even if a transition fires before our listeners hook in.
  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(syncReactState, 800);
    return () => window.clearInterval(id);
  }, [enabled, syncReactState]);

  // Periodic RTT probe — sends `{__ping: ts}` on every OPEN data channel
  // every 3 s. The receiver echoes back `{__pong: ts}`; we compute RTT in
  // the dc.onmessage handler. Surfaced through peerStates[id].rttMs.
  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => {
      const now = Date.now();
      peersRef.current.forEach((slot) => {
        if (slot.dataChannel?.readyState === "open") {
          try { slot.dataChannel.send(JSON.stringify({ __ping: now })); }
          catch (e) { void e; }
        }
      });
    }, 3000);
    return () => window.clearInterval(id);
  }, [enabled]);

  // Full teardown when the hook unmounts or `enabled` flips off.
  useEffect(() => {
    if (enabled) return undefined;
    const peersMap = peersRef.current;
    return () => {
      // Stop mic first so the OS mic indicator goes away ASAP.
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      [...peersMap.keys()].forEach((id) => dropPeer(id));
      setLocalMicEnabled(false);
    };
  }, [enabled, dropPeer]);

  // Hard cleanup on actual unmount.
  useEffect(() => {
    const peersMap = peersRef.current;
    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      peersMap.forEach((slot) => {
        try { slot.dataChannel?.close(); } catch (e) { void e; }
        try { slot.pc?.close(); } catch (e) { void e; }
        try { slot.audioEl?.remove(); } catch (e) { void e; }
      });
      peersMap.clear();
    };
  }, []);

  return {
    peerStreams: peerStates,
    localMicEnabled,
    localStreamErr,
    toggleMic,
    setPeerMuted,
    sendData,
    sendBinary,
    handleSignal,
    peerCount: Object.keys(peerStates).length,
    /** Count of peers with an open data channel — useful for UI badges
     *  ("CRDT · fast" vs "CRDT · sync"). */
    openChannelCount: Object.values(peerStates).filter((s) => s?.dataChannelOpen).length,
  };
}
